/* Zailarah - global core (window.ZL).
   Extends the bootstrap object printed inline by layout/theme.liquid (routes, shop, currency.active, designMode,
   promo data, strings, settings). Never overwrites a key that already holds a function / object set by another script
   (e.g. ZL.cart, owned by zl-cart.js). Plain ES2019, no dependencies. Never throws while loading. */
(function () {
  'use strict';

  var win = window;
  var doc = document;
  var ZL = (win.ZL = win.ZL || {});

  /* ---------------------------------------------------------------- helpers */

  function define(obj, key, value) {
    if (obj[key] === undefined || obj[key] === null) obj[key] = value;
  }
  function toNumber(v, fallback) {
    var n = Number(v);
    return isFinite(n) ? n : fallback;
  }

  ZL.routes = ZL.routes || {};
  ZL.shop = ZL.shop || {};
  ZL.strings = ZL.strings || {};
  ZL.settings = ZL.settings || {};
  ZL.currency = ZL.currency || {};
  ZL.promo = ZL.promo || {};

  var shopifyGlobal = win.Shopify || {};
  define(ZL.shop, 'moneyFormat', '{{amount}}');
  define(ZL.shop, 'moneyWithCurrencyFormat', ZL.shop.moneyFormat);
  define(ZL.currency, 'active', (shopifyGlobal.currency && shopifyGlobal.currency.active) || ZL.shop.currency || '');
  ZL.currency.rate = toNumber(shopifyGlobal.currency && shopifyGlobal.currency.rate, 1) || 1;
  ZL.designMode = Boolean(ZL.designMode || shopifyGlobal.designMode);

  /* ---------------------------------------------------------------- money */

  var NAMED_ENTITIES = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ', euro: '€', pound: '£', yen: '¥', cent: '¢' };

  function decodeEntities(str) {
    if (str.indexOf('&') === -1) return str;
    return str.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, function (match, code) {
      if (code.charAt(0) === '#') {
        var point = code.charAt(1).toLowerCase() === 'x' ? parseInt(code.slice(2), 16) : parseInt(code.slice(1), 10);
        try { return String.fromCodePoint(point); } catch (e) { return match; }
      }
      var named = NAMED_ENTITIES[code.toLowerCase()];
      return named === undefined ? match : named;
    });
  }

  /* Shopify money formats may contain markup (<span class=money>) and entities: reduce to plain text. */
  function cleanFormat(format) {
    return decodeEntities(String(format).replace(/<[^>]*>/g, ''));
  }

  /* Formats the ABSOLUTE amount; the sign is added by formatMoney in front of the whole string ("-€97,95", like
     Liquid's money filter) and only when the rounded amount is not zero (never "-0"). */
  function formatNumber(cents, precision, thousands, decimal) {
    var fixed = (Math.abs(cents) / 100).toFixed(precision);
    var parts = fixed.split('.');
    var whole = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
    return { text: whole + (parts[1] ? decimal + parts[1] : ''), zero: Number(fixed) === 0 };
  }

  var PLACEHOLDER = /\{\{\s*(\w+)\s*\}\}/;

  /* stripZeros: drop the decimals when the amount is a whole number of units (moneyShort). */
  function formatMoney(cents, format, stripZeros) {
    if (typeof cents === 'string') cents = cents.replace('.', '');   /* same as Shopify.formatMoney: "97.95" -> 9795 */
    cents = Math.round(toNumber(cents, 0));
    var text = cleanFormat(format || ZL.shop.moneyFormat || '{{amount}}');
    var match = text.match(PLACEHOLDER);
    if (!match) { text = text + '{{amount}}'; match = text.match(PLACEHOLDER); }
    var whole = cents % 100 === 0;
    var value;
    function two() { return stripZeros && whole ? 0 : 2; }
    switch (match[1]) {
      case 'amount_no_decimals': value = formatNumber(cents, 0, ',', '.'); break;
      case 'amount_with_comma_separator': value = formatNumber(cents, two(), '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = formatNumber(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = formatNumber(cents, two(), '\'', '.'); break;
      case 'amount_no_decimals_with_space_separator': value = formatNumber(cents, 0, ' ', ','); break;
      case 'amount_with_space_separator': value = formatNumber(cents, two(), ' ', ','); break;
      case 'amount_with_period_and_space_separator': value = formatNumber(cents, two(), ' ', '.'); break;
      case 'amount':
      default: value = formatNumber(cents, two(), ',', '.');
    }
    var out = text.replace(PLACEHOLDER, function () { return value.text; });
    return cents < 0 && !value.zero ? '-' + out : out;
  }

  define(ZL, 'money', function (cents, format) { return formatMoney(cents, format, false); });
  define(ZL, 'moneyShort', function (cents, format) { return formatMoney(cents, format, true); });
  define(ZL, 'moneyWithCurrency', function (cents) { return formatMoney(cents, ZL.shop.moneyWithCurrencyFormat || ZL.shop.moneyFormat, false); });

  /* ---------------------------------------------------------------- promotions (display / messaging only) */

  var promo = ZL.promo;
  promo.enabled = Boolean(promo.enabled);
  promo.percent = toNumber(promo.percent, 0);
  define(promo, 'label', '');
  promo.freeShipping = promo.freeShipping || {};
  promo.gift = promo.gift || {};
  promo.gift2 = promo.gift2 || {};
  promo.bundle = promo.bundle || {};
  promo.freeShipping.threshold = toNumber(promo.freeShipping.threshold, 0);
  promo.gift.threshold = toNumber(promo.gift.threshold, 0);
  promo.gift2.threshold = toNumber(promo.gift2.threshold, 0);

  /* JS twin of snippets/zl-promo.liquid. Whether a product is on sale comes from the server (data-promo="1"). */
  define(promo, 'salePrice', function (cents) {
    return Math.round(toNumber(cents, 0) * (100 - promo.percent) / 100);
  });

  /* "USD:80" lines (newline or ; separated, decimal comma accepted) -> { USD: 8000 } */
  function parseCurrencyMap(text) {
    var map = {};
    String(text || '').split(/[\n;]+/).forEach(function (line) {
      var i = line.indexOf(':');
      if (i < 1) return;
      var code = line.slice(0, i).trim().toUpperCase();
      var amount = Number(line.slice(i + 1).trim().replace(',', '.'));
      if (code && isFinite(amount) && amount > 0) map[code] = Math.round(amount * 100);
    });
    return map;
  }
  var freeShippingOverrides = parseCurrencyMap(promo.freeShipping.byCurrency);
  var giftOverrides = parseCurrencyMap(promo.gift.byCurrency);
  var gift2Overrides = parseCurrencyMap(promo.gift2.byCurrency);

  /* Shop-currency cents -> cents of the active presentment currency, rounded UP to a whole unit. */
  function presentmentThreshold(shopCents, overrides) {
    var active = ZL.currency.active;
    if (!active || active === ZL.shop.currency) return shopCents;
    if (overrides && overrides[active] != null) return overrides[active];
    var rate = ZL.currency.rate || 1;
    if (rate === 1) return shopCents;
    return Math.ceil((shopCents * rate) / 100 - 1e-9) * 100;
  }
  define(promo, 'freeShippingThreshold', function () { return presentmentThreshold(promo.freeShipping.threshold, freeShippingOverrides); });
  /* the threshold as shown to the visitor: in the currency they shop in ("€90" for $100 at the current rate, what
     checkout applies). promo.freeShipping.label is the Liquid [free_shipping] text, which for a visitor in another
     currency is the shop-currency amount ("$100"): localizeFreeShipping() swaps it for this text on the page. */
  define(promo, 'freeShippingLabel', function () { return ZL.moneyShort(promo.freeShippingThreshold()); });

  function localizeFreeShipping() {
    var from = promo.freeShipping.label;
    var to = promo.freeShippingLabel();
    if (!from || !to || from === to || ZL.currency.active === ZL.shop.currency) return;
    var swap = function (root) {
      if (!root) return;
      if (root.nodeType === 3) {
        if (root.nodeValue.indexOf(from) > -1) root.nodeValue = root.nodeValue.split(from).join(to);
        return;
      }
      if (root.nodeType !== 1 || root.nodeName === 'SCRIPT' || root.nodeName === 'STYLE') return;
      ['aria-label', 'title', 'alt', 'data-zcd-tpl'].forEach(function (name) {
        var value = root.getAttribute(name);
        if (value && value.indexOf(from) > -1) root.setAttribute(name, value.split(from).join(to));
      });
      var walker = doc.createTreeWalker(root, 5 /* elements + text */);
      var node;
      while ((node = walker.nextNode())) {
        if (node.nodeType === 3) { if (node.nodeValue.indexOf(from) > -1) node.nodeValue = node.nodeValue.split(from).join(to); }
        else ['aria-label', 'title', 'alt', 'data-zcd-tpl'].forEach(function (name) {
          var value = node.getAttribute(name);
          if (value && value.indexOf(from) > -1) node.setAttribute(name, value.split(from).join(to));
        });
      }
    };
    var run = function () {
      swap(doc.body);
      /* the cart drawer, filtered grids and section re-renders arrive later */
      new MutationObserver(function (records) {
        records.forEach(function (r) {
          if (r.type === 'characterData') swap(r.target);
          else Array.prototype.forEach.call(r.addedNodes, swap);
        });
      }).observe(doc.body, { childList: true, subtree: true, characterData: true });
    };
    if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', run);
    else run();
  }
  localizeFreeShipping();
  define(promo, 'giftThreshold', function () { return presentmentThreshold(promo.gift.threshold, giftOverrides); });
  define(promo, 'gift2Threshold', function () { return presentmentThreshold(promo.gift2.threshold, gift2Overrides); });

  /* ---------------------------------------------------------------- events (CustomEvents on document) */

  define(ZL, 'on', function (name, fn, options) { doc.addEventListener(name, fn, options); return fn; });
  define(ZL, 'off', function (name, fn, options) { doc.removeEventListener(name, fn, options); });
  define(ZL, 'emit', function (name, detail, target) {
    var event = new CustomEvent(name, { bubbles: true, cancelable: true, detail: detail === undefined ? {} : detail });
    (target || doc).dispatchEvent(event);
    return event;
  });

  /* ---------------------------------------------------------------- network */

  function isPlainObject(value) {
    return Object.prototype.toString.call(value) === '[object Object]';
  }

  /* Resolves with the parsed JSON body (null ONLY for a genuinely empty 2xx body, e.g. 204).
     Rejects with an Error carrying .status and .data (parsed error body, when any) on HTTP errors; with an Error
     carrying .status and .parseError = true when a 2xx body is not JSON (e.g. a password / login page served after a
     redirect); and with the native error on network failure. A plain-object body is sent as JSON. */
  define(ZL, 'fetchJSON', function (url, opts) {
    var options = Object.assign({ credentials: 'same-origin' }, opts || {});
    var headers = Object.assign({ Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, options.headers || {});
    if (isPlainObject(options.body)) {
      options.body = JSON.stringify(options.body);
      if (!headers['Content-Type']) headers['Content-Type'] = 'application/json';
      if (!options.method) options.method = 'POST';
    }
    options.headers = headers;
    return fetch(url, options).then(function (response) {
      return response.text().then(function (text) {
        var data = null;
        var parseFailed = false;
        if (text && text.trim()) { try { data = JSON.parse(text); } catch (e) { data = null; parseFailed = true; } }
        if (!response.ok) {
          var message = (data && (data.description || data.message)) || ZL.strings.error || 'Request failed';
          var error = new Error(typeof message === 'string' ? message : ZL.strings.error || 'Request failed');
          error.status = response.status;
          error.data = data;
          throw error;
        }
        if (parseFailed) {
          var parseError = new Error(ZL.strings.error || 'Request failed');
          parseError.status = response.status;
          parseError.parseError = true;
          throw parseError;
        }
        return data;
      });
    });
  });

  define(ZL, 'fetchHTML', function (url, opts) {
    var options = Object.assign({ credentials: 'same-origin' }, opts || {});
    return fetch(url, options).then(function (response) {
      if (!response.ok) {
        var error = new Error(ZL.strings.error || 'Request failed');
        error.status = response.status;
        throw error;
      }
      return response.text();
    }).then(function (html) {
      return new DOMParser().parseFromString(html, 'text/html');
    });
  });

  /* Section Rendering API. Works for urls that already carry a query string and / or a hash.
     Resolves with the section's wrapper element (#shopify-section-<id>) or null (never rejects). */
  function sectionUrl(url, sectionId) {
    var value = String(url == null ? win.location.pathname + win.location.search : url);
    var hashAt = value.indexOf('#');
    if (hashAt > -1) value = value.slice(0, hashAt);
    value = value.replace(/([?&])section_id=[^&]*(&|$)/, function (m, lead, tail) { return tail ? lead : ''; });
    value = value.replace(/[?&]$/, '');
    return value + (value.indexOf('?') > -1 ? '&' : '?') + 'section_id=' + encodeURIComponent(sectionId);
  }
  define(ZL, 'sectionUrl', sectionUrl);
  define(ZL, 'fetchSection', function (url, sectionId, opts) {
    return ZL.fetchHTML(sectionUrl(url, sectionId), opts).then(function (parsed) {
      var byId = parsed.getElementById('shopify-section-' + sectionId);
      return byId || parsed.querySelector('.shopify-section') || (parsed.body && parsed.body.firstElementChild) || null;
    }).catch(function () { return null; });
  });

  /* ---------------------------------------------------------------- scroll lock (ref-counted by owner) */

  var lockOwners = [];
  var overflowBeforeLock = '';
  define(ZL, 'lockScroll', function (owner) {
    var id = owner == null ? 'default' : owner;
    if (lockOwners.indexOf(id) > -1) return;
    if (!lockOwners.length) overflowBeforeLock = doc.documentElement.style.overflow === 'hidden' ? '' : doc.documentElement.style.overflow;
    lockOwners.push(id);
    doc.documentElement.style.overflow = 'hidden';
  });
  define(ZL, 'unlockScroll', function (owner) {
    var id = owner == null ? 'default' : owner;
    var at = lockOwners.indexOf(id);
    if (at === -1) return;
    lockOwners.splice(at, 1);
    if (!lockOwners.length) doc.documentElement.style.overflow = overflowBeforeLock;
  });
  define(ZL, 'isScrollLocked', function () { return lockOwners.length > 0; });

  /* ---------------------------------------------------------------- focus trap (stack: nested layers supported) */

  var FOCUSABLE = 'a[href],area[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),summary,iframe,audio[controls],video[controls],[contenteditable]:not([contenteditable="false"]),[tabindex]:not([tabindex="-1"])';
  var traps = [];

  function isVisible(el) {
    return Boolean(el.offsetWidth || el.offsetHeight || (el.getClientRects && el.getClientRects().length));
  }
  function focusables(container) {
    return Array.prototype.filter.call(container.querySelectorAll(FOCUSABLE), function (el) {
      return isVisible(el) && !el.closest('[inert]') && !el.closest('[hidden]');
    });
  }
  define(ZL, 'focusables', focusables);

  function safeFocus(el) {
    if (!el || typeof el.focus !== 'function') return;
    try { el.focus({ preventScroll: true }); } catch (e) { try { el.focus(); } catch (e2) { /* not focusable */ } }
  }

  function onTrapKeydown(event) {
    var trap = traps[traps.length - 1];
    if (!trap) return;
    if (event.key === 'Escape' || event.key === 'Esc') {
      if (typeof trap.onEscape === 'function') trap.onEscape(event);
      return;
    }
    if (event.key !== 'Tab') return;
    var items = focusables(trap.container);
    if (!items.length) { event.preventDefault(); safeFocus(trap.container); return; }
    var first = items[0];
    var last = items[items.length - 1];
    var active = doc.activeElement;
    if (!trap.container.contains(active)) { event.preventDefault(); safeFocus(event.shiftKey ? last : first); }
    else if (event.shiftKey && (active === first || active === trap.container)) { event.preventDefault(); safeFocus(last); }
    else if (!event.shiftKey && active === last) { event.preventDefault(); safeFocus(first); }
  }

  /* opts: { initialFocus: Element | selector | false, returnFocus: Element, onEscape: fn(event) } */
  define(ZL, 'trapFocus', function (container, opts) {
    if (!container) return;
    var options = opts || {};
    ZL.releaseFocus(container, { restore: false });
    var trap = { container: container, onEscape: options.onEscape, previous: options.returnFocus || doc.activeElement, addedTabindex: false };
    if (!traps.length) doc.addEventListener('keydown', onTrapKeydown, true);
    traps.push(trap);
    if (options.initialFocus === false) return;
    var target = options.initialFocus;
    if (typeof target === 'string') target = container.querySelector(target);
    if (!target) target = focusables(container)[0];
    if (!target) {
      if (!container.hasAttribute('tabindex')) { container.setAttribute('tabindex', '-1'); trap.addedTabindex = true; }
      target = container;
    }
    safeFocus(target);
  });

  /* opts: { restore: false } keeps focus where it is (default: focus returns to the element focused before the trap). */
  define(ZL, 'releaseFocus', function (container, opts) {
    var at = -1;
    for (var i = traps.length - 1; i >= 0; i--) { if (!container || traps[i].container === container) { at = i; break; } }
    if (at === -1) return;
    var trap = traps.splice(at, 1)[0];
    if (!traps.length) doc.removeEventListener('keydown', onTrapKeydown, true);
    if (trap.addedTabindex) trap.container.removeAttribute('tabindex');
    var restore = !(opts && opts.restore === false);
    if (restore && at === traps.length && trap.previous && doc.contains(trap.previous) && trap.previous !== doc.body) safeFocus(trap.previous);
  });

  /* ---------------------------------------------------------------- small utilities */

  define(ZL, 'debounce', function (fn, ms) {
    var timer;
    function debounced() {
      var self = this;
      var args = arguments;
      clearTimeout(timer);
      timer = setTimeout(function () { fn.apply(self, args); }, ms);
    }
    debounced.cancel = function () { clearTimeout(timer); };
    return debounced;
  });

  /* Leading call, then at most one trailing call per interval. */
  define(ZL, 'throttle', function (fn, ms) {
    var last = 0;
    var timer = null;
    var lastArgs;
    var lastThis;
    function run() { last = Date.now(); timer = null; fn.apply(lastThis, lastArgs); }
    function throttled() {
      lastArgs = arguments;
      lastThis = this;
      var wait = ms - (Date.now() - last);
      if (wait <= 0) { if (timer) { clearTimeout(timer); timer = null; } run(); }
      else if (!timer) timer = setTimeout(run, wait);
    }
    throttled.cancel = function () { clearTimeout(timer); timer = null; };
    return throttled;
  });

  var ESCAPES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
  define(ZL, 'esc', function (str) {
    return String(str == null ? '' : str).replace(/[&<>"']/g, function (c) { return ESCAPES[c]; });
  });

  define(ZL, 'prefersReducedMotion', function () {
    return Boolean(win.matchMedia && win.matchMedia('(prefers-reduced-motion: reduce)').matches);
  });

  /* ---------------------------------------------------------------- reveal on scroll (.zl-reveal -> .is-in, one-shot) */

  var revealObserver = null;
  function revealNow(el) { el.classList.add('is-in'); }
  function revealEnabled() {
    return ZL.settings.animationsReveal !== false && !ZL.designMode && 'IntersectionObserver' in win;
  }
  function getRevealObserver() {
    if (!revealObserver) {
      revealObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) { entry.target.classList.add('is-in'); revealObserver.unobserve(entry.target); }
        });
      }, { rootMargin: '0px 0px -8% 0px' });
    }
    return revealObserver;
  }
  ZL.reveal = ZL.reveal || {};
  define(ZL.reveal, 'observe', function (root) {
    var scope = root && root.querySelectorAll ? root : doc;
    var targets = Array.prototype.slice.call(scope.querySelectorAll('.zl-reveal:not(.is-in)'));
    if (scope !== doc && scope.matches && scope.matches('.zl-reveal:not(.is-in)')) targets.unshift(scope);
    if (!targets.length) return;
    if (!revealEnabled()) { targets.forEach(revealNow); return; }
    var observer = getRevealObserver();
    targets.forEach(function (el) { observer.observe(el); });
  });

  /* ---------------------------------------------------------------- slider progress (track + .zl-progress i) */

  ZL.slider = ZL.slider || {};
  /* Same maths as the prototype: bar width = visible share (min 8%), left = scroll share of the remaining room. */
  define(ZL.slider, 'progress', function (track, barEl) {
    if (!track || !barEl || !track.scrollWidth) return;
    var max = track.scrollWidth - track.clientWidth;
    var width = Math.max((track.clientWidth / track.scrollWidth) * 100, 8);
    barEl.style.width = width + '%';
    barEl.style.left = (max > 0 ? (Math.abs(track.scrollLeft) / max) * (100 - width) : 0) + '%';
  });

  /* ---------------------------------------------------------------- theme editor helper */

  var EDITOR_EVENTS = {
    load: 'shopify:section:load', unload: 'shopify:section:unload', select: 'shopify:section:select',
    deselect: 'shopify:section:deselect', reorder: 'shopify:section:reorder',
    blockSelect: 'shopify:block:select', blockDeselect: 'shopify:block:deselect'
  };
  /* ZL.onSection(el, { select(e){}, deselect(e){}, blockSelect(e){}, blockDeselect(e){}, load(e){}, unload(e){}, reorder(e){} })
     Calls a handler only for editor events that belong to the Shopify section containing `el`.
     Returns an unsubscribe function (call it in disconnectedCallback). */
  define(ZL, 'onSection', function (el, handlers) {
    var bound = [];
    if (!el || !handlers) return function () {};
    Object.keys(EDITOR_EVENTS).forEach(function (key) {
      if (typeof handlers[key] !== 'function') return;
      var listener = function (event) {
        var section = el.closest ? el.closest('.shopify-section') : null;
        var target = event.target;
        if (!target || !target.nodeType) return;
        if (section ? section.contains(target) : el.contains(target) || target.contains(el)) handlers[key](event);
      };
      doc.addEventListener(EDITOR_EVENTS[key], listener);
      bound.push([EDITOR_EVENTS[key], listener]);
    });
    return function () {
      bound.forEach(function (pair) { doc.removeEventListener(pair[0], pair[1]); });
      bound = [];
    };
  });

  /* ---------------------------------------------------------------- boot */

  function boot() {
    try { ZL.reveal.observe(doc); } catch (e) { /* reveal is progressive enhancement */ }
  }
  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', boot);
  else boot();

  doc.addEventListener('shopify:section:load', function (event) {
    try { ZL.reveal.observe(event.target); } catch (e) { /* ignore */ }
  });

  /* Units that inject HTML (filtering, pagination, recommendations, cart sections) announce it with
     ZL.emit('zl:content:replaced', { root: container })  - or dispatch it on the container itself - so injected
     .zl-reveal nodes never stay invisible. Calling ZL.reveal.observe(container) directly is equivalent. */
  doc.addEventListener('zl:content:replaced', function (event) {
    var root = (event.detail && event.detail.root) || event.target;
    try { ZL.reveal.observe(root && root.nodeType === 1 ? root : doc); } catch (e) { /* ignore */ }
  });

  ZL.ready = true;
  try { doc.dispatchEvent(new CustomEvent('zl:ready', { detail: { ZL: ZL } })); } catch (e) { /* very old browsers */ }
})();
